// ============================================================
// WorkspaceModeTabs — the multimodal mode selector.
//
// Modes map to real Square Code lanes (Intake / Documents /
// Agent Build / Meetings / …) and are passed in as config —
// never hardcoded sample modes. Active mode carries its accent;
// inactive modes stay dim until hovered. Local component state
// belongs to the caller (controlled component).
//
//   modes: [{ key, label, glyph?, accent?, badge?, disabled?, hint? }]
// ============================================================

import { useState } from 'react'
import { tint, WORKSPACE_ACCENTS, WORKSPACE_FONT, WORKSPACE_TEXT } from './workspaceTheme'

export default function WorkspaceModeTabs({
  modes = [],
  active,
  onChange,
  align = 'flex-start',
  style: styleOverride,
}) {
  const [hovered, setHovered] = useState(null)

  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        justifyContent: align,
        ...styleOverride,
      }}
    >
      {modes.map((mode) => {
        const accent = mode.accent || WORKSPACE_ACCENTS.cyan
        const isActive = active === mode.key
        const isHover = hovered === mode.key && !mode.disabled
        return (
          <button
            key={mode.key}
            role="tab"
            type="button"
            aria-selected={isActive}
            disabled={mode.disabled}
            title={mode.hint}
            onClick={() => !mode.disabled && onChange?.(mode.key)}
            onMouseEnter={() => setHovered(mode.key)}
            onMouseLeave={() => setHovered(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 14px',
              borderRadius: 8,
              cursor: mode.disabled ? 'not-allowed' : 'pointer',
              background: isActive ? tint(accent, 12) : isHover ? tint(accent, 6) : 'transparent',
              border: `1px solid ${isActive ? tint(accent, 30) : isHover ? tint(accent, 16) : 'transparent'}`,
              transition: 'all 0.25s ease',
              opacity: mode.disabled ? 0.45 : 1,
              fontFamily: WORKSPACE_FONT.mono,
            }}
          >
            {mode.glyph && (
              <span style={{ fontSize: 10, color: isActive ? accent : WORKSPACE_TEXT.muted }}>
                {mode.glyph}
              </span>
            )}
            <span
              style={{
                fontSize: 11,
                fontWeight: isActive ? 700 : 500,
                letterSpacing: '0.06em',
                color: isActive ? accent : isHover ? WORKSPACE_TEXT.secondary : WORKSPACE_TEXT.tertiary,
              }}
            >
              {mode.label}
            </span>
            {mode.badge != null && mode.badge !== 0 && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '1px 5px',
                  borderRadius: 4,
                  background: tint(accent, 14),
                  color: accent,
                }}
              >
                {mode.badge}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
